/**
 * Auth storage: where the Better Auth credentials live on this device.
 *
 * Three entries, all in localStorage so the auth seam can stay synchronous:
 *
 *   - SESSION_TOKEN_KEY: the 60-day sliding session token returned in the
 *     `set-auth-token` header. It is the only credential that cannot be
 *     re-derived, so it is also mirrored into IndexedDB (authTokenBackup.ts):
 *     Safari's ITP can purge localStorage between visits while leaving
 *     IndexedDB alone, and main.tsx restores the mirror before React mounts.
 *   - ACCESS_TOKEN_KEY: the 15-minute JWT minted from the session at
 *     `/auth/token`. Persisted so `getAccessToken()` works straight after a
 *     reload; not mirrored, because it is short-lived and re-minted anyway.
 *   - AUTH_USER_KEY: the signed-in user's profile (id, email, name), for
 *     onboarding and billing screens. Mirrored, so an offline cold start after
 *     an ITP purge still knows who is signed in.
 *
 * Nothing here talks to the network. Every read/write is wrapped so a
 * blocked or throwing storage never breaks an auth code path.
 */

import { clearBackup, mirrorToIndexedDB, removeFromIndexedDB } from './authTokenBackup'

export const SESSION_TOKEN_KEY = 'moly_auth_session_token'
export const ACCESS_TOKEN_KEY = 'moly_auth_access_token'
export const AUTH_USER_KEY = 'moly_auth_user'

export interface AuthUser {
  id: string
  email: string
  name: string
  emailVerified: boolean
  image?: string | null
}

export interface AuthSnapshot {
  isAuthenticated: boolean
  user: AuthUser | null
}

type Listener = () => void

const listeners = new Set<Listener>()

const readItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeItem = (key: string, value: string, mirror: boolean): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage full or blocked: the in-memory copy (access token) or the
    // IndexedDB mirror (session) still carries us through this page load.
  }
  if (mirror) mirrorToIndexedDB(key, value)
}

const removeItem = (key: string): void => {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
  removeFromIndexedDB(key)
}

export const isAuthUser = (value: unknown): value is AuthUser => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.emailVerified === 'boolean'
  )
}

/** Keep only the fields the app uses, so the stored shape stays stable. */
export const toAuthUser = (value: unknown): AuthUser | null => {
  if (!isAuthUser(value)) return null
  return {
    id: value.id,
    email: value.email,
    name: value.name,
    emailVerified: value.emailVerified,
    image: typeof value.image === 'string' ? value.image : null,
  }
}

// ---------------------------------------------------------------------------
// Session token
// ---------------------------------------------------------------------------

export const getSessionToken = (): string | null => readItem(SESSION_TOKEN_KEY) || null

export const setSessionToken = (token: string): void => {
  if (readItem(SESSION_TOKEN_KEY) === token) return
  writeItem(SESSION_TOKEN_KEY, token, true)
  notifyAuthChanged()
}

// ---------------------------------------------------------------------------
// Access token (JWT): memory first, localStorage behind it
// ---------------------------------------------------------------------------

// `undefined` = not read from storage yet on this page load.
let accessTokenCache: string | null | undefined

export const getStoredAccessToken = (): string | null => {
  if (accessTokenCache === undefined) {
    accessTokenCache = readItem(ACCESS_TOKEN_KEY) || null
  }
  return accessTokenCache
}

export const setStoredAccessToken = (token: string): void => {
  accessTokenCache = token
  writeItem(ACCESS_TOKEN_KEY, token, false)
}

export const clearStoredAccessToken = (): void => {
  accessTokenCache = null
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY)
  } catch {
    // ignore
  }
}

/**
 * Re-read the access token from localStorage. Called when another tab
 * refreshed it (storage event), so this tab picks up the newer JWT.
 */
export const reloadAccessTokenFromStorage = (): void => {
  accessTokenCache = undefined
}

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export const getStoredUser = (): AuthUser | null => {
  const raw = readItem(AUTH_USER_KEY)
  if (!raw) return null
  try {
    return toAuthUser(JSON.parse(raw))
  } catch {
    return null
  }
}

export const setStoredUser = (user: AuthUser): void => {
  writeItem(AUTH_USER_KEY, JSON.stringify(user), true)
  notifyAuthChanged()
}

export const clearStoredUser = (): void => {
  removeItem(AUTH_USER_KEY)
  notifyAuthChanged()
}

// ---------------------------------------------------------------------------
// Clearing
// ---------------------------------------------------------------------------

/**
 * Remove the session, JWT and user from localStorage and from the IndexedDB
 * mirror. Resolves once the IndexedDB backup is cleared: callers about to
 * reload MUST await it, or restoreFromIndexedDB() on the next boot would
 * bring the session straight back.
 */
export const clearAuthStorage = (): Promise<void> => {
  accessTokenCache = null
  for (const key of [SESSION_TOKEN_KEY, ACCESS_TOKEN_KEY, AUTH_USER_KEY]) {
    removeItem(key)
  }
  notifyAuthChanged()
  return clearBackup()
}

// ---------------------------------------------------------------------------
// Change notification (for useSyncExternalStore)
// ---------------------------------------------------------------------------

let snapshot: AuthSnapshot | null = null

const computeSnapshot = (): AuthSnapshot => ({
  isAuthenticated: getSessionToken() !== null,
  user: getStoredUser(),
})

/** Stable snapshot of the auth state; a new object only after a change. */
export const getAuthSnapshot = (): AuthSnapshot => {
  if (!snapshot) snapshot = computeSnapshot()
  return snapshot
}

export const notifyAuthChanged = (): void => {
  const next = computeSnapshot()
  const previous = snapshot
  const userChanged = JSON.stringify(previous?.user ?? null) !== JSON.stringify(next.user)
  if (previous && previous.isAuthenticated === next.isAuthenticated && !userChanged) return
  snapshot = next
  for (const listener of listeners) listener()
}

export const subscribeAuth = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
